import classNames from "classnames";
import { ReactNode, useCallback } from "react";
import { Control, FieldPath, FieldValues, get, UseFormRegister, UseFormRegisterReturn, useFormState } from "react-hook-form";
import { MessageDescriptor } from "react-intl";
import { FormFeedback } from "reactstrap";
import formLabelStyles from "shared/components/formInputs/FormLabel.module.scss";
import { Message } from "shared/components/Message";
import { TranslatedRegisterOptions, useTranslatedValidations } from "shared/validations/useTranslatedValidations";

type RegisterRadioHandler<
    TFieldValues extends FieldValues = FieldValues,
    TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = () => Omit<UseFormRegisterReturn<TFieldName>, "ref"> & { innerRef: UseFormRegisterReturn["ref" ]};

type Props<
    TFieldValues extends FieldValues = FieldValues,
    TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    TContext extends object = object> = {
    name: TFieldName;
    control?: Control<TFieldValues, TContext>;
    register: UseFormRegister<TFieldValues>;
    options?: TranslatedRegisterOptions<TFieldValues, TFieldName>;
    children: {
        radios: (registerRadio: RegisterRadioHandler) => ReactNode;
        helpText?: ReactNode;
    };
    label: MessageDescriptor;
    isRequired?: boolean;
};

export function RadioGroup<TFieldValues extends FieldValues = FieldValues, TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>, TContext extends object = object>({
    control,
    name,
    register,
    options,
    children,
    label,
    isRequired,
}: Props<TFieldValues, TFieldName, TContext>) {
    const { errors, touchedFields } = useFormState({ control, name });
    const registerOptions = useTranslatedValidations(options);

    const registerRadio = useCallback(() => {
        const { ref: innerRef, ...rest } = register(name, registerOptions);
        return { innerRef, ...rest };
    }, [register, name, registerOptions]);

    const error = errors?.[name];
    const isInvalid = !!error;
    const isTouched = !!get(touchedFields, name);

    return <fieldset
        role="radiogroup"
        aria-labelledby={`${name}Label`}
        aria-required={isRequired}
        aria-invalid={isInvalid}
        aria-errormessage={isInvalid ? `${name}Errors` : undefined}
        aria-describedby={!!children.helpText ? `${name}HelpText` : undefined}
        className={classNames("row", "form-group", isTouched && ["was-validated", isInvalid ? "invalid" : "valid"])}>
        <legend id={`${name}Label`} className={classNames(formLabelStyles.label, "col-12 col-form-label pt-0")}>
            <Message message={label} />
            {isRequired && <span aria-hidden="true" className={formLabelStyles.required}> *</span>}
        </legend>
        <div className="col-12">
            {children.radios(registerRadio)}
            <FormFeedback id={`${name}Errors`} className={isInvalid ? "d-block" : "d-none"} role="alert">{error?.message}</FormFeedback>
            {children.helpText && <div id={`${name}HelpText`}>{children.helpText}</div>}
        </div>
    </fieldset>;
}
